<template>
	<div>
		<v-btn
			v-bind="btnProps"
			v-test="'share-session-button'"
			v-track="'share-session-button'"
			style="z-index: 1"
			icon="icon-share-contact-stroke"
			aria-label="Share"
			variant="plain"
			:retain-focus-on-click="false"
			@click.stop="init"></v-btn>

		<Dialog
			:dialog-visible="dialog"
			:title="'Share with a User'"
			:agree-text="sharing ? 'Sharing' : 'Share'"
			:cancel-text="'Cancel'"
			:disable-agree-button="sharing"
			:options="{ width: '500px', zIndex: 2000 }"
			@agree="send"
			@cancel="reset">
			<template #dialog-body>
				<v-form ref="form">
					<v-autocomplete
						ref="shareWithUserBox"
						v-model="shareUser"
						variant="outlined"
						v-test="'share-dialog-user-select'"
						no-data-text="No users found."
						item-title="searchLabel"
						item-value="id"
						:loading="loadingUsers"
						return-object
						prepend-inner-icon="icon-user-single"
						:items="teamMemberItems"
						:max-length="60"
						:rules="[userRequiredRule]"
						hint="An email will be sent to the selected user."
						persistent-hint
						@focus="userFieldFocus">
						<template #label><FieldLabel label="Share with a User (Required)" /></template>
						<template #selection="{ item }">
							<span v-if="item.raw.firstname || item.raw.lastname" class="text-truncate">
								{{ item.raw.firstname }} {{ item.raw.lastname }}
							</span>
							<span v-else class="text-truncate">{{ item.raw.email }}</span>
						</template>
						<template #item="{ props, item }">
							<v-list-item v-if="!item.raw.edit" v-bind="getListItemProps(props)">
								<v-list-item-title>
									<span v-if="item.raw.firstname || item.raw.lastname" class="text-truncate">
										{{ item.raw.firstname }} {{ item.raw.lastname }}
									</span>
									<span v-else class="text-truncate">{{ item.raw.email }}</span>
								</v-list-item-title>
								<v-list-item-subtitle>
									<span v-if="item.raw.phone">{{ item.raw.phone }}</span>
								</v-list-item-subtitle>
							</v-list-item>
						</template>
					</v-autocomplete>
				</v-form>
			</template>
		</Dialog>
	</div>
</template>
<script>
	import { blurActiveElement, calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import api from "../api";
	import { hasFreeTrialEnded } from "../helpers/hasFreeTrialEnded.js";
	import Dialog from "./Dialog.vue";
	import { VuetifyValidation } from "@/helpers/Validation";
	import { useToast } from "vue-toastification";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { logWarn } from "@/helpers/logError";
	import { describeThrownValue } from "@/helpers/apiErrorShape";

	export default {
		name: "ShareDialog",
		components: {
			FieldLabel,
			Dialog
		},
		setup() {
			const toast = useToast();
			return { toast };
		},
		props: {
			conversationId: String,
			btnProps: {
				type: Object,
				default: () => ({})
			}
		},
		data() {
			return {
				dialog: false,
				shareUser: null,
				teamMembers: [],
				me: null,
				loadingUsers: false,
				// LSP-20900: bumped by every loadTeamMembers() call so a superseded load (dialog closed and
				// reopened, or a second click) cannot toast its failure or add its users to the newer list.
				teamMembersLoadGeneration: 0,
				userMenuWidth: 0,
				coords: { x: 0, y: 0 },
				sharing: false,
				userRequiredRule: VuetifyValidation.RequiredValidationFieldObject("Please select a user.").isValid
			};
		},
		computed: {
			teamMemberItems() {
				return this.teamMembers.map((user) => {
					let name = `${user.firstname} ${user.lastname}`.trim();

					let searchLabel = name;
					if (user.email) {
						searchLabel = `${searchLabel} ${user.email}`;
					}
					if (user.phone) {
						searchLabel = `${searchLabel} ${user.phone}`;
					}

					return Object.assign({}, user, { name, searchLabel });
				});
			}
		},
		mounted() {
			window.addEventListener("resize", this.adjustUserMenu);
		},
		beforeUnmount() {
			window.removeEventListener("resize", this.adjustUserMenu);
		},
		methods: {
			getListItemProps(props) {
				const { title, ...rest } = props;
				return rest;
			},
			adjustUserMenu() {
				this.$nextTick(() => {
					const el = this.$refs.shareWithUserBox?.$el;
					if (!el) return;
					const r = el.getBoundingClientRect();
					// keep menu the same width as the activator
					this.userMenuWidth = Math.round(r.width);
					this.coords = { x: r.left + window.scrollX, y: r.bottom + window.scrollY };
				});
			},
			async init(evt) {
				evt.currentTarget.blur();
				this.reset();
				this.me = await api.getMe();

				if (await hasFreeTrialEnded(this.me.organization, this.me.organizationSubscription)) {
					this.dialog = false;
					this.$root.$freeTrialEndedDialog.open(this.me.organization);
				} else {
					this.dialog = true;
					await this.loadTeamMembers();
					this.$nextTick(() => this.adjustUserMenu());
				}
			},
			loadTeamMembers() {
				const loadGeneration = ++this.teamMembersLoadGeneration;
				const isCurrentLoad = () => loadGeneration === this.teamMembersLoadGeneration;
				this.teamMembers = [];
				this.loadingUsers = true;
				return api
					.getSharingUsers({ page: 1, pageSize: 1000 })
					.then((result) => {
						if (!isCurrentLoad()) {
							logWarn("ShareDialog: ignored a superseded user load", {
								conversationId: this.conversationId,
								outcome: "succeeded"
							});
							return;
						}
						if (!Array.isArray(result)) {
							throw new Error(`getSharingUsers returned a non-array response (${typeof result})`);
						}
						// LSP-20900: getSharingUsers can return a user whose Account has no roles (or no
						// Account at all). Reading `Roles[0].name` unguarded threw inside forEach and dropped
						// every user after the first role-less one, so skip those users instead.
						const administrators = [];
						let skippedCount = 0;
						result.forEach((user) => {
							const roleName = user?.Account?.Roles?.[0]?.name;
							if (typeof roleName !== "string") {
								skippedCount++;
								return;
							}
							if (roleName.toLowerCase() === "administrator") {
								administrators.push(user);
							}
						});
						this.teamMembers = administrators;
						if (skippedCount > 0) {
							logWarn("ShareDialog: skipped users with no account role while building the share list", {
								skippedCount,
								returnedCount: result.length,
								conversationId: this.conversationId
							});
						}
					})
					.catch((error) => {
						if (!isCurrentLoad()) {
							logWarn("ShareDialog: ignored a superseded user load", {
								conversationId: this.conversationId,
								outcome: "failed"
							});
							return;
						}
						logWarn("ShareDialog: failed to load users to share with", {
							conversationId: this.conversationId,
							error: describeThrownValue(error)
						});
						if (this.dialog) {
							const message = "We couldn't load the people you can share with. Please try again.";
							this.toast.error(message, { timeout: calculateTimeout(message) });
						}
					})
					.finally(() => {
						if (isCurrentLoad()) {
							this.loadingUsers = false;
						}
					});
			},
			async send() {
				try {
					const { valid } = await this.$refs.form.validate();
					if (!valid || !this.shareUser) {
						return;
					}
					this.sharing = true;
					const request = {
						conversationId: this.conversationId,
						url: window.location.toString(),
						userId: this.shareUser.id
					};
					await api.shareConversation(request);
					const successMessage = "Your stuff has been shared successfully.";
					this.toast.success(successMessage, calculateTimeout(successMessage));
					this.reset();
				} catch (err) {
					console.error(err, {
						tags: {
							method: "send",
							file: "ShareDialog"
						},
						extra: {
							conversationId: this.conversationId,
							userId: this.shareUser?.id
						}
					});
					const errorMessage = getMessageFromError(err);
					this.toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				} finally {
					this.sharing = false;
				}
			},
			async reset() {
				this.$refs.form?.reset();
				this.shareUser = null;
				this.sharing = false;
				this.dialog = false;
			},
			onInput() {
				blurActiveElement();
			},
			userFieldFocus() {
				this.adjustUserMenu();
			}
		}
	};
</script>
<style>
	.share-user-menu {
		margin-top: 0 !important; /* creates a gap so the menu never covers the input */
		max-height: 300px; /* optional: keeps list from running too tall on mobile */
		overflow: auto;
		border-radius: 12px !important;
		z-index: 20000 !important;
	}
</style>
